import React, { useEffect, useState } from "react";

interface ConfirmationModalProps {
    isOpen: boolean;
    message: string;
    setIsValveModalOpen: (isOpen: boolean) => void;
    handleOpenValveClick: (comment?: string) => Promise<void>;
    showCommentBox?: boolean;
    commentLabel?: string;
    commentPlaceholder?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    message,
    setIsValveModalOpen,
    handleOpenValveClick,
    showCommentBox = false,
    commentLabel = "Comment",
    commentPlaceholder = "Enter here",
}) => {
    const [comment, setComment] = useState("");
    const [isCommentTooLong, setIsCommentTooLong] = useState(false);
    const MAX_COMMENT_CHARS = 30;

    useEffect(() => {
        if (isOpen) {
            setComment("");
            setIsCommentTooLong(false);
        }
    }, [isOpen]);

    if (!isOpen) return null; // Don't render if not open

    return (
        <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={() => setIsValveModalOpen(false)} // Close on background click
        >
            <div
                className={`bg-darkcolor-600 rounded-lg shadow-lg w-full ${showCommentBox ? "sm:max-w-lg md:max-w-2xl lg:max-w-2xl" : "sm:max-w-sm md:max-w-md lg:max-w-md"}`}
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
            >
                <div className={`flex items-center justify-between ${showCommentBox ? "px-6 py-4 border-b border-darkcolor-500" : "p-8 pb-0"}`}>
                    <h2 className="text-lg font-semibold text-white">{message}</h2>
                    <button
                        type="button"
                        aria-label="Close"
                        className="text-white opacity-80 hover:opacity-100 transition-opacity"
                        onClick={() => setIsValveModalOpen(false)}
                    >
                        ✕
                    </button>
                </div>

                {showCommentBox && (
                    <div className="px-6 py-5">
                        <div className="text-white mb-2 text-sm">{commentLabel}</div>
                        <textarea
                            className="w-full resize-none rounded-md p-4 bg-darkcolor-800 text-white border border-darkcolor-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder={commentPlaceholder}
                            value={comment}
                            onChange={(e) => {
                                const next = e.target.value ?? "";
                                if (next.length > MAX_COMMENT_CHARS) {
                                    setIsCommentTooLong(true);
                                    setComment(next.slice(0, MAX_COMMENT_CHARS));
                                } else {
                                    setIsCommentTooLong(false);
                                    setComment(next);
                                }
                            }}
                            rows={5}
                        />
                        {isCommentTooLong && (
                            <div className="mt-2 text-sm" style={{ color: "#EF4444" }}>
                                Max limit : {MAX_COMMENT_CHARS}
                            </div>
                        )}
                    </div>
                )}

                {!showCommentBox && (
                    <div className="flex justify-center gap-4 mt-6 p-8 pt-6">
                        <button
                            type="button"
                            onClick={() => setIsValveModalOpen(false)}
                            style={{ float: "right", backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }}
                            className="px-6 py-3 bg-gray-400 text-gray-900 rounded-md hover:bg-gray-500 transition-all"
                        >
                            No
                        </button>

                        <button
                            type="button"
                            onClick={async () => {
                                await handleOpenValveClick(undefined);
                                setIsValveModalOpen(false);
                            }}
                            style={{ float: "right", backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }}
                            className="px-6 py-3 bg-primary-700 text-white rounded-md hover:bg-primary-800 transition-all"
                        >
                            Yes
                        </button>
                    </div>
                )}

                {showCommentBox && (
                    <div className="px-6 pb-6 pt-2 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsValveModalOpen(false)}
                            style={{ backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }}
                            className="px-6 py-2.5 rounded-md text-white transition-all hover:opacity-90"
                        >
                            No
                        </button>

                        <button
                            type="button"
                            disabled={isCommentTooLong}
                            onClick={async () => {
                                if (isCommentTooLong) return;
                                await handleOpenValveClick(comment);
                                setIsValveModalOpen(false);
                            }}
                            style={{ backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }}
                            className={`px-6 py-2.5 rounded-md text-white transition-all ${isCommentTooLong ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
                        >
                            Yes, proceed
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConfirmationModal;
